import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from "@mui/material";
import apiService from "../../service/apiService";
import { FaRegEdit, FaEye, FaTrash } from "react-icons/fa";
import { Pagination } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import UploadIcon from '@mui/icons-material/Upload';
import { Download } from "@mui/icons-material";
import { format, parseISO } from 'date-fns';
import { debounce } from "lodash";

const Fpc = () => {
  const navigate = useNavigate();
  
  // Essential state variables only
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [pageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [totalItems, setTotalitems] = useState(0);
  const [totalpage, setTotalpage] = useState(0);
  const [fponame, setfponame] = useState('');
  const [address, setAddress] = useState('');
  const [totalShareCapital, setTotalShareCapital] = useState(0);
  const [dateOfIncorporation, setDateOfIncorporation] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [shareHolders, setShareHolders] = useState(0);
  const [business, setBusiness] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [years, setYears] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  
  const [formData, setFormData] = useState({
    fpoName: '',
    dateOfIncorporation: '',
    registrationNumber: '',
    address: '',
    totalShareCapital: '',
    shareHolders: '',
    district: 0,
    year: '',
    turnover: 0
  });

  // Essential useEffects
  useEffect(() => {
    if (selectedRow) {
      setFormData({
        fpoName: selectedRow.name || '',
        dateOfIncorporation: selectedRow.dateOfIncorporation || '',
        registrationNumber: selectedRow.registrationNumber || '',
        address: selectedRow.address || '',
        totalShareCapital: selectedRow.totalShareCapital || '',
        shareHolders: selectedRow.shareHolders || '',
        year: selectedRow.year || year,
        district: selectedRow.district || 0,
        turnover: selectedRow.turnover || 0
      });
    }
  }, [selectedRow]);

  const fetchYears = async () => {
    try {
      const response = await apiService.get("/years/get-all");
      setYears(response.data);
      const currentYear = new Date().getFullYear();
      const defaultYear = response.data.find((yr) => yr.yearValue === currentYear);
      setYear(defaultYear ? defaultYear.yearValue : response.data[0].yearValue);
    } catch (error) {
      console.error("Error fetching years:", error);
    }
  }

  useEffect(() => {
    fetchYears();
  }, []);

  const fetchData = async () => {
    try {
      const response = await apiService.post("/fpo/get-all-fpo-index-data", {
        fpoName: fponame || "",
        registrationNumber: registrationNumber || "",
        business: business || "",
        address: address || "",
        shareHolders: Number(shareHolders) || 0,
        totalShareCapital: Number(totalShareCapital) || 0,
        dateOfIncorporation: dateOfIncorporation || "",
        pageNumber: page,
        pageSize: pageSize,
        year: year
      });

      if (response.data.status && response.data.body && response.data.body.data) {
        const data = response.data.body.data;
        const formattedData = data.map((item) => ({
          id: item.fpoid,
          fpoId: item.fpoId,
          name: item.fpoName,
          address: item.address,
          totalShareCapital: item.totalShareCapital,
          dateOfIncorporation: item.dateOfIncorporation,
          registrationNumber: item.registrationNumber,
          blocks: item.blocks || "Not Available",
          shareHolders: item.shareHolders,
          turnover: item.turnover,
          district: item.district,
          producerGroups: item.producerGroups || "Not Available",
          business: item.business?.replace(/\t/g, "").trim() || "Not Available",
        }));

        setRows(formattedData);
        setTotalitems(response.data.body.recordsCount);
        setTotalpage(response.data.body.pageCount);
      } else {
        setRows([]);
        setTotalitems(0);
        setTotalpage(0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setRows([]);
      setTotalitems(0);
      setTotalpage(0);
    }
  };

  const debouncedFetchData = useCallback(debounce(fetchData, 500), [
    fponame, registrationNumber, business, address, shareHolders, totalShareCapital, 
    dateOfIncorporation, year, page, pageSize
  ]);

  useEffect(() => {
    debouncedFetchData();
    return () => debouncedFetchData.cancel();
  }, [debouncedFetchData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleTurnoverChange = (e) => {
    const { value } = e.target;
    if (/^\d*$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        turnover: value
      }));
    }
  };

  const handleView = (row) => {
    setSelectedRow(row);
    setOpen(true);
  };

  const handleEdit = (id) => {
    const rowToEdit = rows.find((row) => row.fpoId === id);
    if (rowToEdit) {
      setSelectedRow(rowToEdit);
      setOpenEdit(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedRow(null);
  };

  const handleDeleteRow = async (fpoId) => {
    if (window.confirm("Are you sure you want to delete this FPC?")) {
      try {
        const response = await apiService.delete(`/fpo/delete/${fpoId}`);
        if (response.status === 200) {
          alert("FPC deleted successfully");
          fetchData();
        }
      } catch (error) {
        console.error("Error deleting the FPC:", error);
        alert("An error occurred while deleting the FPC");
      }
    }
  };

  const handleSubmit = async () => {
    const isConfirmed = window.confirm("Are you sure you want to update this FPC?");
    if (!isConfirmed) return;

    try {
      const fpoId = selectedRow?.fpoId || formData.fpoId;
      if (!fpoId) {
        alert("FPO ID is missing. Unable to update.");
        return;
      }

      await apiService.put(`/fpo/update/${fpoId}`, {
        fpoName: formData.fpoName,
        dateOfIncorporation: formData.dateOfIncorporation,
        registrationNumber: formData.registrationNumber,
        address: formData.address,
        totalShareCapital: Number(formData.totalShareCapital),
        shareHolders: formData.shareHolders,
        year: formData.year,
        districtId: formData.district,
        turnover: Number(formData.turnover)
      });

      alert("FPO updated successfully!");
      handleCloseEdit();
      fetchData();
    } catch (error) {
      console.error("Error updating data:", error);
      alert("An error occurred while updating the FPO.");
    }
  };

  const handleExcelUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const confirmUpload = window.confirm("Are you sure you want to upload this file?");
    if (!confirmUpload) return;

    const formData = new FormData();
    formData.append("file", file);

    apiService.post('/upload/fpo-upload', formData, {
      headers: {
        Accept: '*/*',
        'Content-Type': 'multipart/form-data',
      },
    })
      .then(() => {
        alert("File uploaded successfully!");
        fetchData();
      })
      .catch((error) => {
        console.error('Error uploading file:', error);
        alert("Failed to upload file. Please try again.");
      });
  };

  const handleDownloadExcel = () => {
    const headers = [
      "SlNo", "FPOName", "DateOfIncorporation", "RegistrationNumber", "Address",
      "TotalShareCapital", "ShareHolders", "Business", "NumberOfMale", "NumberOfFemale"
    ];
    const worksheetData = [headers];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, "FPO_Upload_Format.xlsx");
  };

  const exportToExcel = async () => {
    try {
      const response = await apiService.post("/fpo/get-all-fpo-index-data", {
        fpoName: fponame || "", registrationNumber: registrationNumber || "",
        business: business || "", address: address || "",
        shareHolders: Number(shareHolders) || 0, totalShareCapital: Number(totalShareCapital) || 0,
        dateOfIncorporation: dateOfIncorporation || "", pageNumber: page,
        pageSize: totalItems, year: year
      });

      if (response.data.status && response.data.body && response.data.body.data) {
        const data = response.data.body.data;
        const headers = [
          "Name", "Date Of Incorporation", "Registration No", "Address", "Blocks(no.)",
          "PG(no.)", "Total Share-capital", "Shareholders", "Business"
        ];

        const excelData = data.map((item) => [
          item.fpoName,
          item.dateOfIncorporation ? format(parseISO(item.dateOfIncorporation), "dd-MM-yyyy") : "Not Available",
          item.registrationNumber, item.address, item.blocks || "Not Available",
          item.producerGroups || "Not Available", item.totalShareCapital,
          item.shareHolders, item.business || "Not Available"
        ]);

        const worksheetData = [headers, ...excelData];
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
        XLSX.writeFile(workbook, "FPO_Data.xlsx");
      }
    } catch (error) {
      console.error("Error fetching or exporting FPO data:", error);
    }
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
  };

  const renderPaginationItems = () => {
    const paginationItems = [];
    const maxPagesToShow = 7;
    const startPages = 3;
    const endPages = 2;

    if (totalpage <= maxPagesToShow) {
      for (let pages = 1; pages <= totalpage; pages++) {
        paginationItems.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }
    } else {
      for (let pages = 1; pages <= startPages; pages++) {
        paginationItems.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }

      if (page > startPages + 1) {
        paginationItems.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }

      const middlePages = [];
      const startMiddlePage = Math.max(page - 1, startPages + 1);
      const endMiddlePage = Math.min(page + 1, totalpage - endPages);

      for (let pages = startMiddlePage; pages <= endMiddlePage; pages++) {
        middlePages.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }

      paginationItems.push(...middlePages);

      if (page < totalpage - endPages - 1) {
        paginationItems.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }

      for (let pages = totalpage - endPages + 1; pages <= totalpage; pages++) {
        paginationItems.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }
    }

    return paginationItems;
  };

  return (
    <Box style={{
      backgroundColor: "#f8f9fa",
      overflowY: "auto"
    }}>
      {/* Header - Same design as ffd.jsx */}
      <div style={{
        backgroundColor: "#ffffff",
        borderBottom: "3px solid #007bff",
        boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
      }}>
        <div className="container-fluid">
          <div className="d-flex justify-content-between align-items-center py-3 px-4">
            <div className="d-flex align-items-center">
              <div>
                <h1 style={{
                  fontSize: "1.8rem",
                  fontWeight: "600",
                  color: "#2c3e50",
                  margin: "0"
                }}>
                  FPC Management
                </h1>
              </div>
            </div>

            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center">
                <label style={{
                  fontWeight: "500",
                  color: "#495057",
                  marginRight: "10px",
                  fontSize: "14px"
                }}>
                  Year:
                </label>
                <select
                  className="form-select"
                  value={year || 0}
                  onChange={(e) => setYear(Number(e.target.value))}
                  style={{
                    border: "1px solid #ced4da",
                    borderRadius: "6px",
                    padding: "6px 12px",
                    fontSize: "14px",
                    minWidth: "100px",
                    backgroundColor: "#fff"
                  }}
                >
                  <option value={0} disabled>Select Year</option>
                  {years.map((yr) => (
                    <option key={yr.yearId} value={yr.yearValue}>
                      {yr.yearValue}
                    </option>
                  ))}
                </select>
              </div>

              <Button
                variant="contained"
                onClick={() => navigate('/addfpc')}
                style={{
                  backgroundColor: "#28a745",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                  textTransform: "none"
                }}
              >
                + Add New
              </Button>
              <Button
                variant="contained"
                component="label"
                startIcon={<UploadIcon />}
                style={{
                  backgroundColor: "#6c757d",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                  textTransform: "none"
                }}
              >
                Upload Excel
                <input
                  type="file"
                  accept=".xlsx, .xls"
                  hidden
                  onChange={handleExcelUpload}
                />
              </Button>
              <Button
                variant="contained"
                onClick={handleDownloadExcel}
                style={{
                  backgroundColor: "#17a2b8",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                  textTransform: "none"
                }}
              >
                Download Format
              </Button>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={exportToExcel}
                style={{
                  backgroundColor: "#007bff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                  textTransform: "none"
                }}
              >
                Export Excel
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Same design as ffd.jsx */}
      <div className="container-fluid" style={{ padding: "20px" }}>
        <div style={{
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 160px)"
        }}>
          <div style={{
            flex: "1",
            overflowY: "auto",
            overflowX: "auto"
          }}>
            <div className="table-responsive">
              <table className="table table-hover mb-0" style={{
                minWidth: "1600px",
                backgroundColor: "#fff"
              }}>
                <thead style={{
                  backgroundColor: "#007bff",
                  color: "white",
                  position: "sticky",
                  top: "0",
                  zIndex: "10"
                }}>
                  <tr>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      textAlign: "center",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "60px"
                    }}>
                      SL
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "150px"
                    }}>
                      Name
                      <br />
                      <input
                        type="text"
                        placeholder="Search"
                        className="form-control form-control-sm mt-2"
                        value={fponame}
                        onChange={(e) => { setfponame(e.target.value); setPage(1); }}
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "#333",
                          padding: "4px 8px"
                        }}
                      />
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "150px"
                    }}>
                      Date Of Incorporation
                      <br />
                      <input
                        type="date"
                        className="form-control form-control-sm mt-2"
                        value={dateOfIncorporation}
                        onChange={(e) => { setDateOfIncorporation(e.target.value); setPage(1); }}
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "#333",
                          padding: "4px 8px"
                        }}
                      />
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "140px"
                    }}>
                      Registration No
                      <br />
                      <input
                        type="text"
                        placeholder="Search"
                        className="form-control form-control-sm mt-2"
                        value={registrationNumber}
                        onChange={(e) => { setRegistrationNumber(e.target.value); setPage(1); }}
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "#333",
                          padding: "4px 8px"
                        }}
                      />
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "150px"
                    }}>
                      Address
                      <br />
                      <input
                        type="text"
                        placeholder="Search"
                        className="form-control form-control-sm mt-2"
                        value={address}
                        onChange={(e) => { setAddress(e.target.value); setPage(1); }}
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "#333",
                          padding: "4px 8px"
                        }}
                      />
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "100px",
                      textAlign: "center"
                    }}>
                      Blocks (no.)
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "100px",
                      textAlign: "center"
                    }}>
                      PG (no.)
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "120px"
                    }}>
                      Shareholder (no.)
                      <br />
                      <input
                        type="text"
                        placeholder="Search"
                        className="form-control form-control-sm mt-2"
                        value={shareHolders}
                        onChange={(e) => { setShareHolders(e.target.value); setPage(1); }}
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "#333",
                          padding: "4px 8px"
                        }}
                      />
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "140px"
                    }}>
                      Total Share Capital (₹)
                      <br />
                      <input
                        type="text"
                        placeholder="Search"
                        className="form-control form-control-sm mt-2"
                        value={totalShareCapital}
                        onChange={(e) => { setTotalShareCapital(e.target.value); setPage(1); }}
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "#333",
                          padding: "4px 8px"
                        }}
                      />
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "100px",
                      textAlign: "center"
                    }}>
                      Turnover(₹)
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      minWidth: "150px"
                    }}>
                      Business Name
                      <br />
                      <input
                        type="text"
                        placeholder="Search"
                        className="form-control form-control-sm mt-2"
                        value={business}
                        onChange={(e) => { setBusiness(e.target.value); setPage(1); }}
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "#333",
                          padding: "4px 8px"
                        }}
                      />
                    </th>
                    <th style={{
                      padding: "15px 12px",
                      fontWeight: "600",
                      fontSize: "13px",
                      textAlign: "center",
                      minWidth: "120px"
                    }}>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: "13px" }}>
                  {rows.map((data, index) => (
                    <tr key={index} style={{
                      backgroundColor: index % 2 === 0 ? "#f8f9fa" : "#ffffff",
                      borderBottom: "1px solid #dee2e6"
                    }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#e3f2fd";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#f8f9fa" : "#ffffff";
                      }}>
                      <td style={{
                        padding: "12px 8px",
                        textAlign: "center",
                        fontWeight: "600",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6"
                      }}>
                        {(page - 1) * 10 + index + 1}
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6",
                        fontWeight: "500"
                      }}>
                        {data.name}
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6",
                        fontWeight: "500"
                      }}>
                        {data.dateOfIncorporation ? format(parseISO(data.dateOfIncorporation), "dd-MM-yyyy") : "N/A"}
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6"
                      }}>
                        {data.registrationNumber}
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6",
                        fontSize: "12px"
                      }}>
                        {data.address}
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        textAlign: "center",
                        fontWeight: "600",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6"
                      }}>
                        {data.blocks}
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        textAlign: "center",
                        fontWeight: "600",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6"
                      }}>
                        {data.producerGroups}
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        textAlign: "center",
                        fontWeight: "600",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6"
                      }}>
                        <span style={{
                          backgroundColor: '#28a745',
                          color: 'white',
                          padding: "3px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "600"
                        }}>
                          {data.shareHolders}
                        </span>
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        textAlign: "center",
                        fontWeight: "600",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6"
                      }}>
                        <span style={{
                          backgroundColor: '#007bff',
                          color: 'white',
                          padding: "3px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "600"
                        }}>
                          {data.totalShareCapital}
                        </span>
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        textAlign: "center",
                        fontWeight: "600",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6"
                      }}>
                        <span style={{
                          backgroundColor: '#ffc107',
                          color: '#212529',
                          padding: "3px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "600"
                        }}>
                          {data.turnover || 0}
                        </span>
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        color: "#495057",
                        borderRight: "1px solid #dee2e6",
                        fontSize: "12px"
                      }}>
                        {data.business}
                      </td>
                      <td style={{
                        padding: "12px 8px",
                        textAlign: "center"
                      }}>
                        <div style={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                          <button
                            className="btn btn-sm btn-info"
                            onClick={() => handleView(data)}
                            title="View"
                            style={{
                              padding: "4px 8px",
                              fontSize: "12px"
                            }}
                          >
                            <FaEye />
                          </button>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleEdit(data.fpoId)}
                            title="Edit"
                            style={{
                              padding: "4px 8px",
                              fontSize: "12px"
                            }}
                          >
                            <FaRegEdit />
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteRow(data.fpoId)}
                            title="Delete"
                            style={{
                              padding: "4px 8px",
                              fontSize: "12px"
                            }}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Pagination - Same design as ffd.jsx */}
        <div className='d-flex justify-content-between align-items-center py-3 px-3' style={{
          backgroundColor: "#f8f9fa",
          borderTop: "1px solid #dee2e6"
        }}>
          <div>
            <span style={{
              color: "#6c757d",
              fontSize: "14px"
            }}>
              Showing <strong>{(page - 1) * 10 + 1}</strong> to <strong>{Math.min(page * 10, totalItems)}</strong> of <strong>{totalItems}</strong> entries
            </span>
          </div>
          <div>
            <Pagination className="mb-0">
              <Pagination.First onClick={() => handlePageChange(1)} disabled={page === 1} />
              <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 1} />
              {renderPaginationItems()}
              <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === totalpage} />
              <Pagination.Last onClick={() => handlePageChange(totalpage)} disabled={page === totalpage} />
            </Pagination>
          </div>
        </div>
      </div>

      {/* View Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Farmers Producer Company Management</DialogTitle>
        <DialogContent>
          {selectedRow && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" color="textSecondary">
                  <strong>Name:</strong> {selectedRow.name}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" color="textSecondary">
                  <strong>Date of Incorporation:</strong>{" "}
                  {selectedRow.dateOfIncorporation ? format(parseISO(selectedRow.dateOfIncorporation), "dd-MM-yyyy") : "Not Available"}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" color="textSecondary">
                  <strong>Address:</strong> {selectedRow.address}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" color="textSecondary">
                  <strong>Number of Blocks:</strong> {selectedRow.blocks}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" color="textSecondary">
                  <strong>Producer Groups:</strong> {selectedRow.producerGroups}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" color="textSecondary">
                  <strong>Total Share Capital:</strong> {selectedRow.totalShareCapital}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" color="textSecondary">
                  <strong>Shareholders:</strong> {selectedRow.shareHolders}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" color="textSecondary">
                  <strong>Turnover:</strong> {selectedRow.turnover}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" color="textSecondary">
                  <strong>Business Name:</strong> {selectedRow.business}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEdit} onClose={handleCloseEdit} fullWidth>
        <DialogTitle>Edit FPC</DialogTitle>
        <DialogContent>
          {selectedRow && (
            <>
              <FormControl fullWidth margin="dense">
                <InputLabel id="edit-year-label">Year</InputLabel>
                <Select
                  labelId="edit-year-label"
                  label="Year"
                  name="year"
                  value={formData.year}
                  onChange={handleInputChange}
                >
                  <MenuItem value="" disabled>Select Year</MenuItem>
                  {years.map((yr) => (
                    <MenuItem key={yr.yearId} value={yr.yearValue}>
                      {yr.yearValue}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                autoFocus
                margin="dense"
                label="FPO Name"
                fullWidth
                variant="outlined"
                name="fpoName"
                value={formData.fpoName}
                onChange={handleInputChange}
              />
              <TextField
                margin="dense"
                label="Date of Incorporation"
                fullWidth
                variant="outlined"
                name="dateOfIncorporation"
                type="date"
                value={formData.dateOfIncorporation}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                margin="dense"
                label="Registration Number"
                fullWidth
                variant="outlined"
                name="registrationNumber"
                value={formData.registrationNumber}
                onChange={handleInputChange}
              />
              <TextField
                margin="dense"
                label="Address"
                fullWidth
                variant="outlined"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
              />
              <TextField
                margin="dense"
                label="Total Share Capital"
                fullWidth
                variant="outlined"
                name="totalShareCapital"
                value={formData.totalShareCapital}
                onChange={handleInputChange}
              />
              <TextField
                margin="dense"
                label="Share Holders"
                fullWidth
                variant="outlined"
                name="shareHolders"
                value={formData.shareHolders}
                onChange={handleInputChange}
              />
              <TextField
                margin="dense"
                label="Turnover"
                fullWidth
                variant="outlined"
                name="turnover"
                value={formData.turnover}
                onChange={handleTurnoverChange}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} color="primary">Cancel</Button>
          <Button onClick={handleSubmit} color="primary">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Fpc;